import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import FooterQuick from "./FooterQuick";
import { Mail, Phone, ArrowRight, Clock } from "lucide-react";
import { apiFetch } from "../../services/api/Navbar";
import { apisubscriberFetch } from "../../services/api/Subcribe";
import { apiAboutPage } from "../../services/api/Pages";
import AddressItem from "./AddressItem";
import ContactItem from "./ContactItem";
import SectionHeading from "./SectionHeading";
import SocialLinks from "./SocialMedia";

interface FooterPage {
  name: string;
  url: string;
}

const quickLinks = [
  {
    label: "About Us",
    to: "pages/introduction",
  },
  {
    label: "Olympic Values",
    to: "pages/olympic-values",
  },
  {
    label: "Anti Doping",
    to: "member-units/commissions",
  },
  {
    label: "Latest News",
    to: "news",
  },
  {
    label: "Upcoming Events",
    to: "events",
  },
  {
    label: "Sports Gallery",
    to: "gallery",
  },
  {
    label: "Blogs",
    to: "news",
  },
];

const defaultPolicyPages = [
  {
    title: "Privacy Policy",
    slug: "privacy-policy",
  },
  {
    title: "Terms of Use",
    slug: "terms-of-use",
  },
  {
    title: "Cookie Policy",
    slug: "cookie-policy",
  },
];

export function Footer() {
  const [footerPages, setFooterPages] = useState<FooterPage[]>([]);
  const [settings, setSettings] = useState<any>({});
  const [policyPages, setPolicyPages] = useState<PolicyPage[]>([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const res = await apiFetch("menu");
        const data = res?.data?.data;
        setSettings(data?.settings || {});
        setFooterPages(data?.footer_pages || []);
      } catch (err) {
        console.error("Footer fetch error:", err);
      }
    };

    fetchFooter();
  }, []);

  const handleSubscribe = async () => {
    if (!email.trim()) {
      setMessage("Please enter your email address.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");

      const response = await apisubscriberFetch("subscriber", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(response.data?.message || "Subscription failed.");
      }

      setMessage(response.data?.message || "Successfully subscribed!");

      setEmail("");
    } catch (error) {
      setMessage(
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  interface PolicyPage {
    id: number;
    title: string;
    slug: string;
    body: string;
    image?: string;
  }
  return (
    <footer className="bg-white text-black border-t-2">
      <div className="min-w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.5fr_0.5fr_1fr_1fr] gap-4 text-center md:text-left">
          {/* Organization Info */}
          <div className="space-y-4">
            <div className="flex space-x-3">
              {settings.favicon && (
                <div className="h-24 w-24 mt-2 flex-shrink-0">
                  <img
                    src={settings.favicon}
                    alt="favicon"
                    className="object-contain"
                  />
                </div>
              )}

              <div className="text-start w-full px-2 py-4">
                <SectionHeading
                  title={
                    <h4 className="font-semibold text-2xl">
                      {settings.app_name || "Haryana Olympic Association"}
                    </h4>
                  }
                  subtitle={
                    <div className="mb-2">Excellence in Sport Excellence.</div>
                  }
                />

                <p className="text-black text-lg leading-relaxed mt-5  font-semibold">
                  {settings.description || ""}
                </p>
              </div>
            </div>

            <SocialLinks settings={settings} />
          </div>

          {/* Quick Links */}
          <div className="px-2">
            <SectionHeading
              title={<h4 className="font-semibold text-2xl">Quick Links</h4>}
            />
            <FooterQuick quickLinks={quickLinks} />
          </div>

          {/* Contact Info */}
          <div className="">
            <SectionHeading
              title={<h4 className="font-semibold text-2xl">Contact Us</h4>}
            />
            <div className="space-y-3">
              <AddressItem address={settings.address} />

              {settings.phone && (
                <ContactItem icon={Phone}>
                  <a
                    href={`tel:${settings.phone}`}
                    className="hover:text-black transition-colors"
                  >
                    {settings.phone}
                  </a>
                </ContactItem>
              )}

              {settings.email && (
                <ContactItem icon={Mail}>
                  <a
                    href={`mailto:${settings.email}`}
                    className="break-all hover:text-black transition-colors"
                  >
                    {settings.email}
                  </a>
                </ContactItem>
              )}
            </div>
          </div>

          {/* Newsletter */}
          <div id="newsletter">
            <SectionHeading
              title={<h4 className="font-semibold text-2xl">Stay Updated</h4>}
            />

            <p className="text-lg text-black mb-4 font-semibold">
              Subscribe to our newsletter for the latest Olympic news and
              updates.
            </p>

            <div className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />

                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSubscribe();
                    }
                  }}
                  placeholder="Enter your email"
                  className="border-yellow-700 placeholder-gray-400 pl-10"
                />
              </div>

              <Button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-xl py-4 px-2"
              >
                {loading ? "Subscribing..." : "Subscribe Now"}
                {!loading && <ArrowRight />}
              </Button>

              {message && <p className="text-sm font-medium">{message}</p>}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-black font-semibold text-center lg:text-left">
              {settings.footer_text}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              {(policyPages?.length ? policyPages : defaultPolicyPages).map(
                (page) => (
                  <Link
                    key={page.slug}
                    to={`/pages/${page.slug}`}
                    className="text-sm text-black hover:text-black transition-colors"
                  >
                    {page.title}
                  </Link>
                ),
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
